import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import './RoomChat.css';

const MESSAGE_PAGE_SIZE = 30;

function getAvatarColor(id) {
  const palette = ['#7c3aed', '#2563eb', '#059669', '#b45309', '#db2777', '#0891b2', '#dc2626', '#65a30d'];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

function groupMessages(messages) {
  const groups = [];
  const FIVE_MIN_MS = 5 * 60 * 1000;
  for (const msg of messages) {
    const last = groups[groups.length - 1];
    const lastMsg = last?.messages[last.messages.length - 1];
    if (last && last.sender_id === msg.sender_id &&
        new Date(msg.created_at) - new Date(lastMsg.created_at) < FIVE_MIN_MS) {
      last.messages.push(msg);
    } else {
      groups.push({ sender_id: msg.sender_id, messages: [msg] });
    }
  }
  return groups;
}

const DMChat = ({ otherUser, currentUser, onMarkRead }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [activeMessageMenuId, setActiveMessageMenuId] = useState(null);
  const [deleteTargetMessage, setDeleteTargetMessage] = useState(null);
  const [deletingMessage, setDeletingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesViewportRef = useRef(null);
  const inputRef = useRef(null);
  const channelRef = useRef(null);
  const shouldStickToBottomRef = useRef(false);
  const initialLoadCompleteRef = useRef(false);

  useEffect(() => {
    initialLoadCompleteRef.current = false;
    shouldStickToBottomRef.current = false;
    loadMessages();
    markAsRead();
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [otherUser.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shouldStickToBottomRef.current || !messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({ behavior: initialLoadCompleteRef.current ? 'smooth' : 'auto' });
    shouldStickToBottomRef.current = false;
    initialLoadCompleteRef.current = true;
  }, [messages]);

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.message-actions')) {
        setActiveMessageMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const focusId = requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(focusId);
  }, [otherUser.id]);

  const loadMessages = async () => {
    const { data } = await supabase
      .from('direct_messages')
      .select('*')
      .or(
        `and(sender_id.eq.${currentUser.id},receiver_id.eq.${otherUser.id}),` +
        `and(sender_id.eq.${otherUser.id},receiver_id.eq.${currentUser.id})`
      )
      .order('created_at', { ascending: false })
      .limit(MESSAGE_PAGE_SIZE);

    const initialMessages = (data || []).slice().reverse();
    shouldStickToBottomRef.current = true;
    setMessages(initialMessages);
    setHasMoreMessages((data || []).length === MESSAGE_PAGE_SIZE);

    const channel = supabase
      .channel(`dm-${currentUser.id}-${otherUser.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'direct_messages', filter: `receiver_id=eq.${currentUser.id}` },
        (payload) => {
          const msg = payload.new;
          if (msg.sender_id !== otherUser.id) return;
          shouldStickToBottomRef.current = true;
          setMessages(prev => [...prev, msg]);
          markAsRead();
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'direct_messages' },
        (payload) => {
          setMessages(prev => prev.filter(msg => msg.id !== payload.old.id));
        }
      )
      .subscribe();

    channelRef.current = channel;
  };

  const markAsRead = () => {
    supabase.rpc('mark_dm_read', { p_sender_id: otherUser.id }).then(() => {});
    if (onMarkRead) onMarkRead(otherUser.id);
  };

  const loadOlderMessages = async () => {
    if (loadingOlderMessages || !hasMoreMessages || messages.length === 0 || !messagesViewportRef.current) return;

    setLoadingOlderMessages(true);
    const viewport = messagesViewportRef.current;
    const previousScrollHeight = viewport.scrollHeight;
    const previousScrollTop = viewport.scrollTop;
    const oldestMessageCreatedAt = messages[0]?.created_at;

    const { data } = await supabase
      .from('direct_messages')
      .select('*')
      .or(
        `and(sender_id.eq.${currentUser.id},receiver_id.eq.${otherUser.id}),` +
        `and(sender_id.eq.${otherUser.id},receiver_id.eq.${currentUser.id})`
      )
      .lt('created_at', oldestMessageCreatedAt)
      .order('created_at', { ascending: false })
      .limit(MESSAGE_PAGE_SIZE);

    const olderMessages = (data || []).slice().reverse();
    setHasMoreMessages((data || []).length === MESSAGE_PAGE_SIZE);

    if (olderMessages.length > 0) {
      setMessages(prev => [...olderMessages, ...prev]);
      requestAnimationFrame(() => {
        if (!messagesViewportRef.current) return;
        const nextScrollHeight = messagesViewportRef.current.scrollHeight;
        messagesViewportRef.current.scrollTop = nextScrollHeight - previousScrollHeight + previousScrollTop;
      });
    }

    setLoadingOlderMessages(false);
  };

  const canDeleteMessage = (msg) => {
    if (String(msg.id).startsWith('opt-')) return false;
    return msg.sender_id === currentUser.id;
  };

  const handleDeleteMessage = async () => {
    if (!deleteTargetMessage) return;
    setDeletingMessage(true);
    const { error } = await supabase.from('direct_messages').delete().eq('id', deleteTargetMessage.id);
    setDeletingMessage(false);
    if (!error) {
      setMessages(prev => prev.filter(msg => msg.id !== deleteTargetMessage.id));
      setDeleteTargetMessage(null);
      setActiveMessageMenuId(null);
    }
  };

  const handleSend = async () => {
    const content = newMessage.trim();
    if (!content) return;

    const optimistic = {
      id: `opt-${Date.now()}`,
      sender_id: currentUser.id,
      receiver_id: otherUser.id,
      content,
      created_at: new Date().toISOString(),
    };
    shouldStickToBottomRef.current = true;
    setMessages(prev => [...prev, optimistic]);
    setNewMessage('');

    await supabase.from('direct_messages').insert({
      sender_id: currentUser.id,
      receiver_id: otherUser.id,
      content,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleMessagesScroll = () => {
    if (!messagesViewportRef.current || loadingOlderMessages || !hasMoreMessages) return;
    if (messagesViewportRef.current.scrollTop <= 80) {
      loadOlderMessages();
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return (
      d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' · ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  };

  const otherName = otherUser.display_name || otherUser.username || 'Unknown';
  const currentName = currentUser.display_name || currentUser.username || 'Me';

  return (
    <div className="room-chat-view">

      {/* ── Topbar ── */}
      <div className="room-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 30, height: 30, borderRadius: '50%',
              background: getAvatarColor(otherUser.id),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0,
            }}
          >
            {otherName.charAt(0).toUpperCase()}
          </div>
          <span className="room-topbar-name">{otherName}</span>
          {otherUser.username && (
            <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>@{otherUser.username}</span>
          )}
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="messages-scroll-root" style={{ flex: 1, overflowY: 'auto' }}>
        <div
          ref={messagesViewportRef}
          className="messages-scroll-viewport"
          onScroll={handleMessagesScroll}
        >
          {messages.length === 0 ? (
            <div className="empty-state">
              <span className="empty-state-icon">💬</span>
              <span>No messages yet. Say hello!</span>
            </div>
          ) : (
            <div className="messages-list">
              {loadingOlderMessages && (
                <div className="messages-history-status">Loading earlier messages…</div>
              )}
              {!loadingOlderMessages && hasMoreMessages && (
                <div className="messages-history-status">Scroll up to load earlier messages</div>
              )}
              {groupMessages(messages).map(group => {
                const firstMsg = group.messages[0];
                const isOwn = firstMsg.sender_id === currentUser.id;
                const senderName = isOwn ? currentName : otherName;
                const avatarId = isOwn ? currentUser.id : otherUser.id;
                return (
                  <div key={firstMsg.id} className="message-group">
                    <div
                      style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: getAvatarColor(avatarId),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      {senderName.charAt(0).toUpperCase()}
                    </div>
                    <div className="message-group-body">
                      <div className="message-header">
                        <span className="message-sender-name">{senderName}</span>
                        <span className="message-time">{formatTime(firstMsg.created_at)}</span>
                      </div>
                      {group.messages.map(msg => {
                        const canDelete = canDeleteMessage(msg);
                        return (
                          <div key={msg.id} className="message-item">
                            <p className="message-text">{msg.content}</p>
                            <div className={`message-actions${activeMessageMenuId === msg.id ? ' menu-open' : ''}`}>
                              <button
                                className="message-action-trigger"
                                onClick={() => setActiveMessageMenuId(current => current === msg.id ? null : msg.id)}
                                aria-label="Message actions"
                              >
                                <span className="message-action-dots">•••</span>
                              </button>
                              {activeMessageMenuId === msg.id && (
                                <div className="message-action-menu">
                                  {canDelete ? (
                                    <button
                                      className="message-action-item danger"
                                      onClick={() => {
                                        setDeleteTargetMessage(msg);
                                        setActiveMessageMenuId(null);
                                      }}
                                    >
                                      Delete Message
                                    </button>
                                  ) : (
                                    <div className="message-action-empty">No actions available yet</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* ── Input ── */}
      <div className="chat-input-area">
        <textarea
          ref={inputRef}
          className="chat-textarea"
          rows={2}
          value={newMessage}
          onChange={e => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Message ${otherName}…`}
        />
        <button
          className="btn btn-primary btn-send"
          onClick={handleSend}
          disabled={!newMessage.trim()}
        >
          Send
        </button>
      </div>

      {deleteTargetMessage && (
        <div className="room-modal-backdrop" onClick={() => !deletingMessage && setDeleteTargetMessage(null)}>
          <div className="room-modal" onClick={e => e.stopPropagation()}>
            <h3 className="room-modal-title">Delete Message</h3>
            <p className="room-modal-subtitle">Are you sure you want to delete this message?</p>
            <div className="message-delete-preview">
              {deleteTargetMessage.content}
            </div>
            <div className="delete-modal-actions">
              <button
                className="dash-btn dash-btn-secondary"
                onClick={() => setDeleteTargetMessage(null)}
                disabled={deletingMessage}
              >
                Cancel
              </button>
              <button
                className="dash-btn dash-btn-danger"
                onClick={handleDeleteMessage}
                disabled={deletingMessage}
              >
                {deletingMessage ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DMChat;
